import { useState, useEffect, useRef } from "react";
import useFetchWithMsal from "../../../hooks/useFetchWithMsal";
import { protectedResources } from "../../../authConfig";
import { useUser } from "../../../contexts/UserContext";

const NOT_FOUND_INDEX = -1;
const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

// ── local cache ───────────────────────────────────────────────────────────────
// localStorage persists across browser restarts — survives F5, new tabs,
// closing and reopening the browser. Key is user-specific to avoid data
// leaking between users on a shared machine.
// Strategy: always show cached data instantly, always refresh in the background.
function cacheKey(email) { return `approveLines:orders:${email}`; }

function readCache(email) {
  try {
    const raw = localStorage.getItem(cacheKey(email));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeCache(email, orders) {
  try {
    localStorage.setItem(cacheKey(email), JSON.stringify(orders));
  } catch { /* localStorage full or unavailable */ }
}

function clearCache(email) {
  try { localStorage.removeItem(cacheKey(email)); } catch { /* ignore */ }
}
// ─────────────────────────────────────────────────────────────────────────────

const useApproveLines = (tabValue) => {
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });
  const { user } = useUser();

  const [ordersList, setOrdersList]       = useState([]);
  const [loading, setLoading]             = useState(true); // true until first data arrives
  const [loadingMore, setLoadingMore]     = useState(false);
  const [approvingOrderId, setApprovingOrderId] = useState(null);
  const [serialNumbers, setSerialNumbers] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success", title: "" });

  const fetchGenRef = useRef(0);

  // Fetches all pages from Priority, updating the list after each batch.
  // silent=true: background refresh — no spinner, list replaces atomically at end.
  // silent=false: foreground load — spinner shown, list grows after each batch.
  const _loadAllPages = async (generation, silent = false) => {
    if (!silent) {
      setLoading(true);
      setOrdersList([]);
    }

    try {
      let allOrders = [];
      let skip = 0;
      let isFirstBatch = true;

      while (skip !== null) {
        if (fetchGenRef.current !== generation) return;

        const response = await execute(
          "GET",
          `${BASE_URL}/api/approveLines/getOrders?skip=${skip}`
        );

        if (fetchGenRef.current !== generation) return;

        const batch = (response.ordersToApprove || []).map((order) => ({
          ...order,
          _uniqueId: order.ordname,
        }));

        allOrders = [...allOrders, ...batch];

        if (!silent) {
          // Foreground: grow the list after each batch so user sees orders arriving
          setOrdersList([...allOrders]);
          if (isFirstBatch) {
            setLoading(false);
            setLoadingMore(!!response.hasMore);
            isFirstBatch = false;
          }
        }

        // Write after each batch — if user refreshes mid-load, cache has something
        writeCache(user.email, allOrders);

        skip = response.hasMore ? response.nextSkip : null;
      }

      if (silent) {
        // Background: replace the cached view atomically when fully loaded
        setOrdersList(allOrders);
        setLoadingMore(false);
      }
    } catch (error) {
      if (fetchGenRef.current === generation && !silent) {
        showError("Failed to load orders. Please try again later.");
      }
    } finally {
      if (fetchGenRef.current === generation) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const fetchOrders = async (forceRefresh = false) => {
    if (!user?.email) return;
    const generation = ++fetchGenRef.current;

    if (!forceRefresh) {
      const cached = readCache(user.email);
      if (cached?.length) {
        // Show cached data instantly, refresh in background to pick up new orders
        setOrdersList(cached);
        setLoading(false);
        setLoadingMore(true);
        _loadAllPages(generation, true);
        return;
      }
    }

    // No cache (first ever load) or forced refresh after approval → show spinner
    await _loadAllPages(generation, false);
  };

  useEffect(() => {
    if (user?.email) fetchOrders();
  }, [tabValue, user?.email]);

  const handleQuantityChange = (orderIndex, lineIndex, event) => {
    const newQuant = event.target.value !== "" ? parseInt(event.target.value) : 0;
    const originalQuant = ordersList[orderIndex].lines[lineIndex].quant;

    if (newQuant <= originalQuant) {
      updateOrderLine(orderIndex, lineIndex, { newQuant });
    } else {
      showError(`New quantity cannot exceed original quantity: ${originalQuant}`);
    }
  };

  const handleLineCheckboxChange = (orderIndex, lineIndex) => {
    const line = ordersList[orderIndex].lines[lineIndex];
    const checked = line.checked === undefined ? true : !line.checked;
    updateOrderLine(orderIndex, lineIndex, { checked });
  };

  const handleDeltaCheckboxChange = (orderIndex, lineIndex) => {
    const line = ordersList[orderIndex].lines[lineIndex];
    if (line.deltaEnabled) {
      updateOrderLine(orderIndex, lineIndex, { deltaEnabled: false });
    } else {
      const deltaQuantity = line.quant - (line.newQuant !== undefined ? line.newQuant : line.quant);
      updateOrderLine(orderIndex, lineIndex, { deltaEnabled: true, deltaQuantity });
    }
  };

  const updateOrderLine = (orderIndex, lineIndex, updates) => {
    const newOrdersList = [...ordersList];
    newOrdersList[orderIndex].lines[lineIndex] = {
      ...newOrdersList[orderIndex].lines[lineIndex],
      ...updates,
    };
    setOrdersList(newOrdersList);
  };

  const updateOrderData = (orderIndex, lineIndex, newData) => {
    setOrdersList((prevOrders) => {
      const newOrders = [...prevOrders];
      newOrders[orderIndex].lines[lineIndex] = {
        ...newOrders[orderIndex].lines[lineIndex],
        ...newData,
      };
      return newOrders;
    });
  };

  const fetchSerialNumbers = async (partname) => {
    try {
      const response = await execute(
        "POST",
        `${BASE_URL}/api/approveLines/getSerialNumbers`,
        { partname }
      );
      if (response?.serials) {
        setSerialNumbers((prev) => ({ ...prev, [partname]: response.serials }));
      }
    } catch {
      showError("Failed to load serial numbers");
    }
  };

  const handleConfirmCheckedLines = async (order) => {
    const checkedLines = order.lines.filter((line) => line.checked);
    if (checkedLines.length === 0) {
      showError("Please select lines to approve");
      return;
    }

    const { changedAmountLines, hasZeroQuantityLine, hasBatchOrNote } =
      analyzeCheckedLines(checkedLines, user.email);

    setApprovingOrderId(order._uniqueId);

    try {
      const responseData = await sendApprovalRequest(order, checkedLines, {
        changedAmountLines,
        hasZeroQuantityLine,
        hasBatchOrNote,
      });

      if (responseData?.warning) {
        showWarning(responseData.message || 'השחרור בוצע, אך לא ניתן לאמת את יצירת שורת הדלתא — אנא בדקי ב-Priority לפני ניסיון חוזר על מנת למנוע כפילויות.');
      } else {
        showSuccess("Order lines approved successfully");
      }

      // Remove approved order immediately after API succeeds
      const updatedOrders = ordersList.filter(o => o._uniqueId !== order._uniqueId);
      setOrdersList(updatedOrders);
      writeCache(user.email, updatedOrders);

      // Silent background sync to pick up any other status changes
      const generation = ++fetchGenRef.current;
      setLoadingMore(true);
      _loadAllPages(generation, true);
    } catch (error) {
      showError(error.msg || error.message || "Failed to approve order lines");
      if (error.status === 409) return; // approval already running — keep selections as-is
      setOrdersList((prevOrders) => {
        const orderIndex = prevOrders.findIndex((o) => o._uniqueId === order._uniqueId);
        if (orderIndex === NOT_FOUND_INDEX) return prevOrders;
        const updatedOrders = [...prevOrders];
        updatedOrders[orderIndex] = {
          ...updatedOrders[orderIndex],
          lines: updatedOrders[orderIndex].lines.map((line) => ({
            ...line,
            checked: false,
            deltaEnabled: false,
          })),
        };
        return updatedOrders;
      });
    } finally {
      setApprovingOrderId(null);
    }
  };

  const analyzeCheckedLines = (checkedLines, userEmail) => {
    let changedAmountLines = false;
    let hasZeroQuantityLine = false;
    let hasBatchOrNote = false;

    checkedLines.forEach((line) => {
      line.approvedBy = userEmail;
      if (line.newQuant !== undefined && line.newQuant !== line.quant) changedAmountLines = true;
      if (line.newQuant === 0) hasZeroQuantityLine = true;
      if (line.batch || line.note) hasBatchOrNote = true;
    });

    return { changedAmountLines, hasZeroQuantityLine, hasBatchOrNote };
  };

  const sendApprovalRequest = async (order, checkedLines, flags) => {
    const { changedAmountLines, hasZeroQuantityLine, hasBatchOrNote } = flags;

    if (order.itsTomorrow) await handleSendEmail({ order, reason: "tomorrow" });

    if (hasZeroQuantityLine) {
      checkedLines.forEach((line) => {
        if (line.newQuant === 0) handleSendEmail({ order, line, reason: "zeroQuant" });
      });
    }

    const endpoint = !changedAmountLines && !hasBatchOrNote
      ? "/api/approveLines/approve"
      : "/api/approveLines/approveWithAmoountChanges";

    const payload = !changedAmountLines && !hasBatchOrNote
      ? { order, checkedLines }
      : { order: { ...order, lines: checkedLines } };

    const response = await execute("PATCH", `${BASE_URL}${endpoint}`, payload);

    if (response.status !== 200 && response.status !== 400) {
      throw new Error(response.error || "Unknown error occurred");
    }

    return response;
  };

  const handleToggleReviewed = async (order) => {
    const orderIndex = ordersList.findIndex((o) => o._uniqueId === order._uniqueId);
    if (orderIndex === NOT_FOUND_INDEX) return;

    const isCurrentlyReviewed = order.reviewed;
    const endpoint = isCurrentlyReviewed
      ? "/api/approveLines/unsetReviewed"
      : "/api/approveLines/setReviewed";

    try {
      await execute("PATCH", `${BASE_URL}${endpoint}`, { orderNumber: order.ordname });

      const newOrdersList = [...ordersList];
      newOrdersList[orderIndex] = { ...newOrdersList[orderIndex], reviewed: !isCurrentlyReviewed };
      setOrdersList(newOrdersList);
    } catch {
      showError("Failed to update reviewed status");
    }
  };

  const handleSendEmail = async ({ order, line, reason }) => {
    try {
      await execute("POST", `${BASE_URL}/api/approveLines/sendEmail`, {
        order: { ...order, reason },
        line,
      });
    } catch {
      showError("Failed to send email notification");
    }
  };

  const showError   = (message) => setSnackbar({ open: true, message, severity: "error" });
  const showSuccess = (message) => setSnackbar({ open: true, message, severity: "success" });
  const showWarning = (message) => setSnackbar({ open: true, message, severity: "warning", title: "נדרשת בדיקה"});

  return {
    ordersList,
    loading,
    loadingMore,
    approvingOrderId,
    serialNumbers,
    snackbar,
    setSnackbar,
    handleQuantityChange,
    handleLineCheckboxChange,
    handleDeltaCheckboxChange,
    handleConfirmCheckedLines,
    handleToggleReviewed,
    fetchSerialNumbers,
    updateOrderData,
    fetchOrders,
  };
};

export default useApproveLines;
